import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loading, ErrorView } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import FlowBuilder from "@/components/automation/FlowBuilder";
import { ThresholdsEditor, StageRulesEditor, LostJunkEditor, RuleTester } from "@/components/automation/ConfigPanels";

// Two grouped screens share this component. The `group` prop (set by the route)
// controls which internal tabs, title and subtitle are shown. Internal tab state
// lives in the ?tab= query param so it survives refresh / deep-links.
const GROUPS = {
  qualification: {
    title: "Lead Qualification",
    subtitle: "Configure qualification forms, scoring thresholds, and test qualification outcomes.",
    tabs: ["qualification-builder", "quality-thresholds", "rule-tester"],
  },
  stage: {
    title: "Stage Rules",
    subtitle: "Control how leads move across stages and how lost/junk outcomes are handled.",
    tabs: ["stage-activity-rules", "lost-junk-rules"],
  },
};
const TAB_LABELS = {
  "qualification-builder": "Qualification Builder",
  "quality-thresholds": "Quality Thresholds",
  "rule-tester": "Rule Tester",
  "stage-activity-rules": "Stage & Activity Rules",
  "lost-junk-rules": "Lost & Junk Rules",
};

export default function AutomationSettings({ group = "qualification" }) {
  const cfgGroup = GROUPS[group] || GROUPS.qualification;
  const { options } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = cfgGroup.tabs.includes(tabParam) ? tabParam : cfgGroup.tabs[0];
  const [flows, setFlows] = useState([]);
  const [config, setConfig] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const setTab = (t) => setSearchParams({ tab: t }, { replace: true });

  const loadAll = useCallback(async () => {
    setError("");
    try {
      const [{ data: f }, { data: c }] = await Promise.all([
        api.get("/automation/admin/flows"),
        api.get("/automation/admin/config"),
      ]);
      setFlows(f.data);
      setConfig(c.data.config);
      setMeta(c.data.meta);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading) return <Loading label="Loading automation settings..." />;
  if (error) return <ErrorView message={error} onRetry={loadAll} />;

  return (
    <div className="space-y-6" data-testid="automation-settings-page">
      <div>
        <h1 className="font-sora text-3xl font-bold text-navy">{cfgGroup.title}</h1>
        <p className="text-sm text-ink/60">{cfgGroup.subtitle}</p>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-sky">
        {cfgGroup.tabs.map((t) => (
          <button
            key={t}
            data-testid={`automation-tab-${t}`}
            onClick={() => setTab(t)}
            className={`-mb-px rounded-t-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === t ? "border-b-2 border-loop text-loop" : "text-ink/60 hover:text-navy"}`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === "qualification-builder" && <FlowBuilder flows={flows} meta={meta} options={options} onChanged={loadAll} />}
      {tab === "quality-thresholds" && config && <ThresholdsEditor config={config} options={options} onSaved={loadAll} />}
      {tab === "rule-tester" && <RuleTester flows={flows} options={options} />}
      {tab === "stage-activity-rules" && config && <StageRulesEditor config={config} options={options} onSaved={loadAll} />}
      {tab === "lost-junk-rules" && config && <LostJunkEditor config={config} options={options} onSaved={loadAll} />}
    </div>
  );
}
